import { Rectangle } from 'pixi.js';
import {
    DynamicInferredDimensions,
    DynamicLevel,
    TextAlignment,
    TextDirection,
} from '../../../../enums';
import type { TextMetrics } from '../metrics/TextMetrics';
import { buildMetricsForText } from '../metrics/TextMetrics';

import {
    DEFAULT_SCALE,
    DEFAULT_TEXT_ALPHA,
    getBoundsIntersection,
    getTotalBounds,
    isBreakingSpace,
} from '../textMetricsHelpers';
import type { TextMetricItemAPI, WordElementMetrics } from '../metrics/WordElementMetrics';
import type { AnimationConfig } from '../../../../libs/AnimationCurveManager';
import { CanvasRenderer } from './BaseTextRenderer';
import type { AnimatableTextProperties, CanvasRendererSettings } from './BaseTextRenderer';
import type { TextStyleRendererSettings } from './TextStyleRenderer';
import { TextStyleRenderer } from './TextStyleRenderer';
import type { TextDynamicGraphicRenderer } from './TextDynamicGraphicRenderer';

export interface TextRendererSettings extends CanvasRendererSettings {
    text: string;
    alignment: TextAlignment;
    highlightAreas: [number, number][];
    boldRanges: [number, number][];
    italicRanges: [number, number][];
    underlineRanges: [number, number][];
    normalStyle: TextStyleRendererSettings;
    highlightStyle: TextStyleRendererSettings;
    referenceToOutputMultiplier: number;
}

/**
 * Renders a block of text.
 */
export class TextRenderer extends CanvasRenderer<TextRendererSettings> {
    text = '';
    highlightAreas: [number, number][] = [];
    boldRanges: [number, number][] = [];
    italicRanges: [number, number][] = [];
    underlineRanges: [number, number][] = [];
    #textMetrics: TextMetrics | null = null;

    alignment = TextAlignment.LEFT;
    #normalStyle: TextStyleRenderer = new TextStyleRenderer();
    #highlightStyle: TextStyleRenderer = new TextStyleRenderer();
    #textWithGraphicsBounds: Rectangle | null = null;

    #referenceToOutputMultiplier = 1;

    // Canvas-based text editing state
    #editingSelectionStart: number | null = null;
    #editingSelectionEnd: number | null = null;
    #caretVisible = true;
    #selectionColor = 'rgba(128, 188, 254, 0.6)';
    #selectionTextColor: string | null = null;
    #caretColor = '#000000';
    #caretWidth = 2;
    #outlineColor: string | null = null;
    #outlineWidth = 2;
    #hideTextContent = false;

    // Shared offscreen canvas for text measurements
    static #measureCanvas: HTMLCanvasElement | null = null;
    static #measureContext: CanvasRenderingContext2D | null = null;

    constructor() {
        super();
        // By default, render highlights as part of the first render pass
        this.#normalStyle.renderHighlighted = true;
        this.#normalStyle.renderHighlightedColor = true;
        this.#highlightStyle.renderHighlighted = false;
    }

    /**
     * Gets the measurement context, creating it if necessary.
     */
    static #getMeasureContext(): CanvasRenderingContext2D {
        if (!TextRenderer.#measureCanvas) {
            TextRenderer.#measureCanvas = document.createElement('canvas');
            TextRenderer.#measureContext = TextRenderer.#measureCanvas.getContext('2d')!;
        }
        return TextRenderer.#measureContext!;
    }

    /**
     * Measures the width of a substring of an element's text using accurate canvas measurements.
     * This is more accurate than partialAdvanceWidth which uses linear interpolation.
     */
    #measureElementTextWidth(element: WordElementMetrics, start: number, end: number): number {
        if (start === end) return 0;
        // Only use cached advanceWidth if it's non-zero (hidden elements have advanceWidth=0)
        if (start === 0 && end === element.text.length && element.advanceWidth > 0) {
            return element.advanceWidth;
        }

        const ctx = TextRenderer.#getMeasureContext();
        element.style.setupContextWithStyle(ctx, 1, {
            bold: element.bold,
            italic: element.italic,
        });

        const substring = element.text.slice(start, end);
        return ctx.measureText(substring).width;
    }

    /**
     * Measures the width from the start of an element to a specific character index.
     */
    #measureElementWidthToChar(element: WordElementMetrics, charIndex: number): number {
        return this.#measureElementTextWidth(element, 0, charIndex);
    }

    /**
     * Gets the X position within an element for a specific character index.
     * For LTR: position is element.x + width from start
     * For RTL: position is element.x + advanceWidth - width from start (mirrored)
     */
    #getElementCharX(element: WordElementMetrics, charIndex: number): number {
        const widthToChar = this.#measureElementWidthToChar(element, charIndex);
        const isRTL = this.normalStyle.textDirection === TextDirection.RTL;

        if (isRTL) {
            return element.x + element.advanceWidth - widthToChar;
        }
        return element.x + widthToChar;
    }

    /**
     * @internal
     */
    _getElementCharX(element: WordElementMetrics, charIndex: number): number {
        return this.#getElementCharX(element, charIndex);
    }

    /**
     * Finds the character index within an element based on x position relative to the element.
     * Returns the character index that best matches the x position.
     * For RTL text, the relativeX is mirrored because characters render right-to-left.
     */
    getCharIndexInElement(element: WordElementMetrics, relativeX: number): number {
        if (element.text.length === 0) return 0;

        const isRTL = this.normalStyle.textDirection === TextDirection.RTL;

        // For RTL text, mirror the relativeX because:
        // - Visual left (relativeX ≈ 0) corresponds to end of string
        // - Visual right (relativeX ≈ advanceWidth) corresponds to start of string
        const effectiveRelativeX = isRTL ? element.advanceWidth - relativeX : relativeX;

        if (effectiveRelativeX <= 0) return 0;
        if (effectiveRelativeX >= element.advanceWidth) return element.text.length;

        const ctx = TextRenderer.#getMeasureContext();
        element.style.setupContextWithStyle(ctx, 1, {
            bold: element.bold,
            italic: element.italic,
        });

        // Binary search for the character position
        let left = 0;
        let right = element.text.length;

        while (left < right) {
            const mid = Math.floor((left + right) / 2);
            const widthToMid = ctx.measureText(element.text.slice(0, mid)).width;

            if (widthToMid < effectiveRelativeX) {
                left = mid + 1;
            } else {
                right = mid;
            }
        }

        // Check if we should round to the previous or next character
        if (left > 0) {
            const widthToPrev = ctx.measureText(element.text.slice(0, left - 1)).width;
            const widthToCurr = ctx.measureText(element.text.slice(0, left)).width;
            const midPoint = (widthToPrev + widthToCurr) / 2;

            if (effectiveRelativeX < midPoint) {
                return left - 1;
            }
        }

        return left;
    }

    override get hasText(): boolean {
        return this.text.length > 0;
    }

    override get canRender(): boolean {
        return !!this.#textMetrics;
    }

    get normalStyle(): TextStyleRenderer {
        return this.#normalStyle;
    }

    get highlightStyle(): TextStyleRenderer {
        return this.#highlightStyle;
    }

    get _styles() {
        return {
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
        };
    }

    get metrics(): TextMetrics | null {
        return this.#textMetrics;
    }

    /**
     * The text metrics, guaranteed to exist. Throws if accessed before the renderer has been built.
     * Use this in code paths that only run after a successful build (e.g. draw).
     */
    get #builtMetrics(): TextMetrics {
        if (this.#textMetrics === null) {
            throw new Error('TextRenderer metrics accessed before build');
        }
        return this.#textMetrics;
    }

    get activeTextBounds(): Rectangle | null {
        return this.#textMetrics?.bounds ?? null;
    }

    get activeTextMaxLineWidth(): number {
        return Math.max(
            ...this.#builtMetrics.lines.map((line) => line.bounds.x + line.advanceWidth),
        );
    }

    get totalBounds(): Rectangle {
        if (this.#textWithGraphicsBounds === null) {
            throw new Error('TextRenderer totalBounds accessed before build');
        }
        return this.#textWithGraphicsBounds;
    }

    /**
     * Sets the editing selection range for canvas-based text editing.
     * When start equals end, only a caret is shown. When different, selection highlight is shown.
     * Set both to null to disable editing display.
     */
    setEditingSelection(start: number | null, end: number | null) {
        this.#editingSelectionStart = start;
        this.#editingSelectionEnd = end;
    }

    /**
     * Gets the current editing selection range.
     * Returns null if no editing selection is active.
     */
    getEditingSelection(): [number, number] | null {
        if (this.#editingSelectionStart === null || this.#editingSelectionEnd === null) {
            return null;
        }
        return [this.#editingSelectionStart, this.#editingSelectionEnd];
    }

    /**
     * Sets the caret visibility for blinking animation.
     */
    setCaretVisible(visible: boolean) {
        this.#caretVisible = visible;
    }

    setCaretColor(color: string) {
        this.#caretColor = color;
    }

    setSelectionBackgroundColor(color: string) {
        this.#selectionColor = color;
    }

    setSelectionTextColor(color: string | null) {
        this.#selectionTextColor = color;
    }

    setEditingOutlineColor(color: string | null) {
        this.#outlineColor = color;
    }

    /**
     * Hides text content while keeping editing decorations (outline, caret, selection) visible.
     * Used during empty editing state so the placeholder character is not drawn.
     */
    setHideTextContent(hide: boolean) {
        this.#hideTextContent = hide;
    }

    override apply(settings: TextRendererSettings) {
        super.apply(settings);

        this.text = settings.text;
        this.highlightAreas = settings.highlightAreas;
        this.boldRanges = settings.boldRanges;
        this.italicRanges = settings.italicRanges;
        this.underlineRanges = settings.underlineRanges;
        this.alignment = settings.alignment;
        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;

        this.#normalStyle.apply(settings.normalStyle);
        this.#highlightStyle.apply(settings.highlightStyle);
    }

    build() {
        this.resetBuild();

        if (this.text.length === 0) {
            return;
        }

        this.#textMetrics = buildMetricsForText({
            text: this.text,
            alignment: this.alignment,
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
            highlightAreas: this.highlightAreas,
            boldRanges: this.boldRanges,
            italicRanges: this.italicRanges,
            underlineRanges: this.underlineRanges,
            wordBreak: true,
            maxWidth: this.maxTextWidth,
            maxHeight: this.maxTextHeight,
            referenceToOutputMultiplier: this.#referenceToOutputMultiplier,
        });

        if (this.#textMetrics.bounds.width === 0 || this.#textMetrics.bounds.height === 0) {
            // TextMetrics has zero width or height. It's possible for empty text assets.
            this.#textWithGraphicsBounds = Rectangle.EMPTY;
        }

        // build the styles and store the bounds of the added graphics
        const graphicBounds: Rectangle[] = [];
        const objectMaskBounds: Rectangle[] = [];
        const stylesByBuildOrder = [this.normalStyle, this.highlightStyle];
        stylesByBuildOrder.forEach((style) => {
            style.graphics.forEach((graphic) => {
                graphic.build(this.#builtMetrics);
                if (!graphic.asMask) {
                    graphicBounds.push(graphic.bounds);
                } else if (
                    graphic.asMask &&
                    graphic.level === DynamicLevel.object &&
                    style === this.normalStyle
                ) {
                    // only object level masks on the normal style clip any graphics
                    objectMaskBounds.push(graphic.bounds);
                }
            });
        });

        // calculate the total bounds of the object - considering graphics, text and object masks
        const textBounds = this.#textMetrics.bounds;
        let objectBounds = getTotalBounds([...graphicBounds, textBounds]);
        if (objectMaskBounds.length > 0) {
            const maskBoundsTotal = getTotalBounds(objectMaskBounds);
            objectBounds = getBoundsIntersection(objectBounds, maskBoundsTotal);
        }
        this.#textWithGraphicsBounds = objectBounds;

        this.#initializeAnimationValues();
    }

    override _onSetCurrentTime() {
        // to be implemented once we start implementing animation
        // it's also possible that this might become a NOOP in this class
    }

    #initializeAnimationValues() {
        Object.values(this._styles).forEach((style) => {
            style.animationConfigurations.forEach((animation) => {
                const numberOfLevelObjects = animation.getNumberOfLevelObjectsForText(
                    this.#builtMetrics,
                );
                animation.initializeValuesToAnimate(numberOfLevelObjects);
            });
            style.graphics.forEach((graphic) => {
                graphic.animationConfigurations.forEach((animation) => {
                    const numberOfLevelObjects =
                        animation.getNumberOfLevelObjectsForGraphics(graphic);
                    animation.initializeValuesToAnimate(numberOfLevelObjects);
                });
            });
        });
    }

    override draw() {
        if (!this.canvas) {
            return;
        }
        if (this.#textMetrics === null) {
            this._resetCanvas();
            return;
        }
        this.#spreadAnimatedValues();
        super.draw();

        this.context.save();
        this.#clipObject();
        if (!this.#hideTextContent) {
            this.#drawGraphics();
            this.#maskAndDrawText();
            this.#maskAndDrawHighlightText();
        }
        this.#drawEditingSelection();
        this.#drawEditingOutline();
        this.#drawEditingCaret();
        this.context.restore();
    }

    #spreadAnimatedValues() {
        Object.values(this._styles).forEach((style) => {
            style.animationConfigurations.forEach((animation) => {
                animation.spreadAnimatedTextValuesToLevels(this.#builtMetrics);
            });
            style.graphics.forEach((graphic) => {
                graphic.animationConfigurations.forEach((animation) => {
                    animation.spreadAnimatedGraphicValuesToLevels();
                });
            });
        });
    }

    /**
     * Apply any scale animation to the canvas context for the provided style.
     * Currently, scaling support is a bit limited:
     *  1. Scale on the normal style only supports object & line levels
     *  2. Scale on the highlight style only supports highlight level
     *  3. Object level scales is always scaled from the center
     *  4. Line and Highlight levels are always scaled from the top left
     */
    #applyScaleAnimation(
        style: TextStyleRenderer,
        level: DynamicLevel,
        metrics: TextMetricItemAPI,
    ) {
        let scaleX = DEFAULT_SCALE;
        let scaleY = DEFAULT_SCALE;
        let scaleAnchorXNormalized = 0.5;
        let scaleAnchorYNormalized = 0.5;
        if (level === DynamicLevel.line || level === DynamicLevel.highlight) {
            scaleAnchorXNormalized = 0;
            scaleAnchorYNormalized = 0;
        }
        const scaleAnchorX = scaleAnchorXNormalized * metrics.bounds.width + metrics.bounds.x;
        const scaleAnchorY = scaleAnchorYNormalized * metrics.bounds.height + metrics.bounds.y;
        style.animationConfigurations
            .filter(
                (config) =>
                    config.level === level &&
                    (config.property === 'scaleX' || config.property === 'scaleY'),
            )
            .forEach((config) => {
                switch (config.property) {
                    case 'scaleX':
                        scaleX = config.computedValues[metrics.globalWordElementIndex];
                        break;
                    case 'scaleY':
                        scaleY = config.computedValues[metrics.globalWordElementIndex];
                        break;
                }
            });
        if (scaleX === 1 && scaleY === 1) {
            return;
        }
        this.context.translate(scaleAnchorX, scaleAnchorY);
        this.context.scale(scaleX, scaleY);
        this.context.translate(-scaleAnchorX, -scaleAnchorY);
    }

    /**
     * Apply the object masks, since they mask everything, including object level graphics.
     */
    #clipObject() {
        const objectMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.object,
            mask: true,
        });
        objectMasks.forEach((mask) => mask.clipElement(this.context, 0));
    }

    /**
     * Draw all the graphics in level order of object, line, word.
     */
    #drawGraphics() {
        const objectGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.object,
            mask: false,
        });
        const lineGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.line,
            mask: false,
        });
        const wordGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.word,
            mask: false,
        });
        const highlightGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.highlight,
            mask: false,
        });

        objectGraphics.forEach((graphic) => graphic.draw(this.context, undefined));
        lineGraphics.forEach((graphic) => graphic.draw(this.context, undefined));
        wordGraphics.forEach((graphic) => graphic.draw(this.context, undefined));
        highlightGraphics.forEach((graphic) => graphic.draw(this.context, undefined));
    }

    /**
     * Draw text, masking it appropriately at each of line and word levels.
     */
    #maskAndDrawText() {
        const lineMasks = this.#filterAndOrderGraphics({ level: DynamicLevel.line, mask: true });
        const wordMasks = this.#filterAndOrderGraphics({ level: DynamicLevel.word, mask: true });
        // Track the current word index, there is no 1:1 mapping from word masks to word metrics due to breaking spaces.
        let ixWord = 0;

        this.#applyScaleAnimation(this.normalStyle, DynamicLevel.object, this.#builtMetrics);
        this.#builtMetrics.lines.forEach((line, ixLine) => {
            this.context.save();
            this.#applyScaleAnimation(this.normalStyle, DynamicLevel.line, line);
            lineMasks.forEach((mask) => mask.clipElement(this.context, ixLine));
            line.visibleWords.forEach((word) => {
                this.context.save();
                wordMasks.forEach((mask) => mask.clipElement(this.context, ixWord));
                ixWord += 1;
                word.elements.forEach((element) => {
                    element.draw(this.context);
                });
                this.context.restore();
            });

            line.words.forEach((word) => {
                if (!isBreakingSpace(word.text)) {
                    return;
                }
                word.elements.forEach((element) => {
                    element.draw(this.context);
                });
            });
            this.context.restore();
        });
    }

    /**
     * Draw the highlight layer text
     */
    #maskAndDrawHighlightText() {
        const highlightMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.highlight,
            mask: true,
        });
        this.#builtMetrics.highlightSpans.forEach((span, spanIndex) => {
            this.context.save();
            this.#applyScaleAnimation(this.highlightStyle, DynamicLevel.highlight, span);
            highlightMasks.forEach((mask) => mask.clipElement(this.context, spanIndex));
            span.elements.forEach((element) => {
                element.draw(this.context, true);
            });
            this.context.restore();
        });
    }

    /**
     * Filter the graphics by level and mask status. Finally, will reverse the list so that we draw the graphics
     * from top to bottom. So the first graphic in the list will be the top-most graphic. This is how we present
     * the order in the editor.
     */
    #filterAndOrderGraphics({
        level,
        mask,
    }: {
        level: DynamicLevel;
        mask: boolean;
    }): TextDynamicGraphicRenderer[] {
        const style = level === DynamicLevel.highlight ? this.highlightStyle : this.normalStyle;
        return style.graphics
            .filter((graphic) => graphic.level === level && graphic.asMask === mask)
            .reverse();
    }

    /**
     * Draws the selection highlight rectangles for canvas-based text editing.
     * Only draws when there's a selection range (start !== end).
     * Uses baselineOffset (fontSize * lineHeight) to match DOM selection height.
     */
    #drawEditingSelection() {
        if (
            this.#editingSelectionStart === null ||
            this.#editingSelectionEnd === null ||
            this.#editingSelectionStart === this.#editingSelectionEnd
        ) {
            return;
        }

        const startIdx = Math.min(this.#editingSelectionStart, this.#editingSelectionEnd);
        const endIdx = Math.max(this.#editingSelectionStart, this.#editingSelectionEnd);

        this.context.fillStyle = this.#selectionColor;

        // Use baselineOffset (fontSize * lineHeight) to match DOM selection height
        const selectionHeight = this.#normalStyle.baselineOffset;

        const selectionRects: { x: number; y: number; width: number; height: number }[] = [];
        const selectedElements: WordElementMetrics[] = [];
        let charIndex = 0;

        for (const line of this.#builtMetrics.lines) {
            // Calculate half-leading to center selection around text (same as text positioning)
            const leading = (selectionHeight - line.height) * 0.5;
            const selectionY = line.ascentY - leading;

            for (const word of line.words) {
                for (const element of word.elements) {
                    const elemStart = charIndex;
                    const elemEnd = charIndex + element.text.length;

                    // Check if this element overlaps with selection
                    if (elemEnd > startIdx && elemStart < endIdx) {
                        // Skip hidden elements (width 0)
                        if (element.advanceWidth === 0) {
                            charIndex = elemEnd;
                            continue;
                        }

                        const selStart = Math.max(0, startIdx - elemStart);
                        const selEnd = Math.min(element.text.length, endIdx - elemStart);

                        const startX = this.#getElementCharX(element, selStart);
                        const endX = this.#getElementCharX(element, selEnd);
                        const x = Math.min(startX, endX);
                        const width = Math.abs(endX - startX);

                        this.context.fillRect(x, selectionY, width, selectionHeight);
                        selectionRects.push({ x, y: selectionY, width, height: selectionHeight });
                        selectedElements.push(element);
                    }
                    charIndex = elemEnd;
                }
            }
        }

        // Redraw selected text with the system's selection text color (HighlightText).
        // This replicates the browser's ::selection behavior: the selection background is drawn
        // first, then the selected text is re-rendered on top with a contrasting color.
        // Without this, on platforms where the selection background is opaque (e.g., Windows),
        // the text would be completely hidden by the selection rectangle.
        if (this.#selectionTextColor && selectionRects.length > 0) {
            this.context.save();
            const clipPath = new Path2D();
            for (const rect of selectionRects) {
                clipPath.rect(rect.x, rect.y, rect.width, rect.height);
            }
            this.context.clip(clipPath);
            for (const element of selectedElements) {
                this.context.save();
                element.draw(this.context, false, this.#selectionTextColor);
                this.context.restore();
            }
            this.context.restore();
        }
    }

    /**
     * Draws a solid outline around the text content area during canvas-based text editing.
     * Only draws when an outline color has been set via setEditingOutlineColor().
     * Uses the same width logic as getNTEContainerBounds() so trailing spaces are included.
     * When text is empty, draws a minimal outline around the caret position.
     */
    #drawEditingOutline() {
        if (!this.#outlineColor) {
            return;
        }

        let x: number;
        let y: number;
        let width: number;
        let height: number;

        const bounds = this.activeTextBounds;
        if (bounds) {
            x = bounds.x;
            y = bounds.y;
            width = Math.max(bounds.width, this.activeTextMaxLineWidth - bounds.x);
            height = bounds.height;
        } else {
            const caretHeight = this.#normalStyle.baselineOffset;
            x = this.#getLineStartXForAlignment();
            y = 0;
            width = this.#caretWidth;
            height = caretHeight;
        }

        this.context.strokeStyle = this.#outlineColor;
        this.context.lineWidth = this.#outlineWidth;
        this.context.strokeRect(x, y, width, height);
    }

    /**
     * Draws the caret (cursor) for canvas-based text editing.
     * Only draws when selection is collapsed (start === end) and caret is visible.
     * Uses the text fill color to match DOM implementation.
     */
    #drawEditingCaret() {
        if (
            this.#editingSelectionStart === null ||
            this.#editingSelectionEnd === null ||
            this.#editingSelectionStart !== this.#editingSelectionEnd ||
            !this.#caretVisible
        ) {
            return;
        }

        const caretIndex = this.#editingSelectionStart;
        const caretPos = this.#getCaretPosition(caretIndex);

        if (caretPos) {
            // Draw simple caret line with text fill color (matching DOM implementation)
            this.context.strokeStyle = this.#caretColor;
            this.context.lineWidth = this.#caretWidth;
            this.context.beginPath();
            this.context.moveTo(caretPos.x, caretPos.y1);
            this.context.lineTo(caretPos.x, caretPos.y2);
            this.context.stroke();
        }
    }

    /**
     * Calculates the caret position for a given character index.
     * Returns the x coordinate and y range for the caret line.
     * Uses line height for consistent caret height across all characters.
     */
    #getCaretPosition(caretIndex: number): { x: number; y1: number; y2: number } | null {
        if (!this.#textMetrics) {
            return null;
        }
        // Use baselineOffset (fontSize * lineHeight) to match DOM caret/selection height
        const caretHeight = this.#normalStyle.baselineOffset;

        // Handle empty text - show caret at starting position based on alignment
        if (this.#textMetrics.lines.length === 0) {
            const x = this.#getLineStartXForAlignment();
            return { x, y1: 0, y2: caretHeight };
        }

        let charIndex = 0;
        let lastVisibleElement: WordElementMetrics | null = null;
        let lastVisibleElementLine = this.#textMetrics.lines[0];
        // Track trailing space elements on the same line as the last visible element
        let trailingSpaceElement: WordElementMetrics | null = null;

        for (const line of this.#textMetrics.lines) {
            // Calculate half-leading to center caret around text (same as text positioning)
            const leading = (caretHeight - line.height) * 0.5;
            const caretY = line.ascentY - leading;

            for (const word of line.words) {
                for (const element of word.elements) {
                    const elemStart = charIndex;
                    const elemEnd = charIndex + element.text.length;

                    // Track the last visible element for x position fallback
                    if (element.advanceWidth > 0) {
                        lastVisibleElement = element;
                        lastVisibleElementLine = line;
                        trailingSpaceElement = null; // Reset trailing spaces when we see a visible element
                    } else if (element.text.trim() === '' && lastVisibleElementLine === line) {
                        // Track hidden trailing spaces on the same line
                        trailingSpaceElement = element;
                    }

                    if (caretIndex >= elemStart && caretIndex < elemEnd) {
                        const charOffset = caretIndex - elemStart;

                        // Check if caret is right after a newline (on a new line)
                        const prevCharIsNewline =
                            caretIndex > 0 && this.text[caretIndex - 1] === '\n';

                        // For hidden elements (advanceWidth=0)
                        if (element.advanceWidth === 0) {
                            // If we're after a newline, position on the new line based on alignment
                            if (prevCharIsNewline && lastVisibleElement) {
                                // Calculate Y position for the new line (same as "end of text" logic)
                                const prevLeading =
                                    (caretHeight - lastVisibleElementLine.height) * 0.5;
                                const newLineY =
                                    lastVisibleElementLine.ascentY - prevLeading + caretHeight;
                                return {
                                    x: this.#getLineStartXForAlignment(),
                                    y1: newLineY,
                                    y2: newLineY + caretHeight,
                                };
                            }
                            // If on the same line as the last visible element, use its end position
                            // For trailing spaces, measure the actual space width to move caret
                            if (lastVisibleElement && lastVisibleElementLine === line) {
                                const isRTL = this.normalStyle.textDirection === TextDirection.RTL;
                                let x = this.#getElementCharX(
                                    lastVisibleElement,
                                    lastVisibleElement.text.length,
                                );
                                // If this is a trailing space, measure the width of characters
                                // so the caret moves when spaces are typed
                                const isTrailingSpace = element.text.trim() === '';
                                if (isTrailingSpace && charOffset > 0) {
                                    const trailingSpaceWidth = this.#measureElementWidthToChar(
                                        element,
                                        charOffset,
                                    );
                                    // For RTL, trailing spaces go left; for LTR, they go right
                                    x += isRTL ? -trailingSpaceWidth : trailingSpaceWidth;
                                }
                                return {
                                    x,
                                    y1: caretY,
                                    y2: caretY + caretHeight,
                                };
                            }
                            // Otherwise (empty line), position based on text alignment
                            return {
                                x: this.#getLineStartXForAlignment(),
                                y1: caretY,
                                y2: caretY + caretHeight,
                            };
                        }

                        // Use RTL-aware positioning for caret
                        const x = this.#getElementCharX(element, charOffset);

                        return { x, y1: caretY, y2: caretY + caretHeight };
                    }
                    charIndex = elemEnd;
                }
            }
        }

        // Caret is at the very end of text
        // Use the actual last line for positioning (important for empty lines after newlines)
        const actualLastLine = this.#textMetrics.lines[this.#textMetrics.lines.length - 1];

        // Check if text ends with double newline - this means user explicitly created an empty line
        // (e.g., by pressing Enter at end of text). Single trailing newline should NOT create a new line.
        const textEndsWithDoubleNewline = this.text.endsWith('\n\n');
        const caretIsAtEndOfText = caretIndex === this.text.length;

        if (textEndsWithDoubleNewline && caretIsAtEndOfText) {
            // Position caret on the new empty line below the last visible line
            const leading = (caretHeight - lastVisibleElementLine.height) * 0.5;
            const y1 = lastVisibleElementLine.ascentY - leading + caretHeight;
            // Calculate x position based on text alignment
            const lineStartX = this.#getLineStartXForAlignment();
            return {
                x: lineStartX,
                y1,
                y2: y1 + caretHeight,
            };
        }

        // If the last line is different from where the last visible element was,
        // it means we have an empty line (e.g., after pressing Enter at end of text)
        if (actualLastLine !== lastVisibleElementLine) {
            // For empty lines, ascentY is Infinity and height is 0.
            // Calculate position based on the previous line's position.
            const prevLeading = (caretHeight - lastVisibleElementLine.height) * 0.5;
            const y1 = lastVisibleElementLine.ascentY - prevLeading + caretHeight;
            // Calculate x position based on text alignment
            const lineStartX = this.#getLineStartXForAlignment();
            return {
                x: lineStartX,
                y1,
                y2: y1 + caretHeight,
            };
        }

        // Caret at end of text on the same line as last visible element
        if (lastVisibleElement) {
            const leading = (caretHeight - lastVisibleElementLine.height) * 0.5;
            const caretY = lastVisibleElementLine.ascentY - leading;
            const isRTL = this.normalStyle.textDirection === TextDirection.RTL;
            let x = this.#getElementCharX(lastVisibleElement, lastVisibleElement.text.length);
            // If there are trailing spaces, measure their width so caret moves
            if (trailingSpaceElement && trailingSpaceElement.text.length > 0) {
                const trailingSpaceWidth = this.#measureElementWidthToChar(
                    trailingSpaceElement,
                    trailingSpaceElement.text.length,
                );
                // For RTL, trailing spaces go left; for LTR, they go right
                x += isRTL ? -trailingSpaceWidth : trailingSpaceWidth;
            }
            return {
                x,
                y1: caretY,
                y2: caretY + caretHeight,
            };
        }

        // Fallback to last line's last element
        const fallbackLine = this.#textMetrics.lines[this.#textMetrics.lines.length - 1];
        if (fallbackLine.words.length > 0) {
            const lastWord = fallbackLine.words[fallbackLine.words.length - 1];
            if (lastWord.elements.length > 0) {
                const lastElement = lastWord.elements[lastWord.elements.length - 1];
                const leading = (caretHeight - fallbackLine.height) * 0.5;
                const caretY = fallbackLine.ascentY - leading;
                return {
                    x: this.#getElementCharX(lastElement, lastElement.text.length),
                    y1: caretY,
                    y2: caretY + caretHeight,
                };
            }
        }

        return null;
    }

    /**
     * Gets the x position where an empty line should start based on text alignment.
     * Uses the text metrics bounds to determine the correct position.
     */
    #getLineStartXForAlignment(): number {
        const lines = this.#textMetrics?.lines;
        if (!lines || lines.length === 0) {
            return 0;
        }

        // Find the first line with content to use as reference
        const referenceLineWithContent = lines.find(
            (line) => line.words.length > 0 && line.bounds.width > 0,
        );

        if (this.alignment === TextAlignment.CENTER) {
            // For CENTER alignment, use the center of a reference line
            // Since all centered lines have their center at maxTextWidth / 2,
            // we can calculate it from any line with content
            if (referenceLineWithContent) {
                return (
                    referenceLineWithContent.bounds.x + referenceLineWithContent.bounds.width / 2
                );
            }
            return this.maxTextWidth / 2;
        } else if (this.alignment === TextAlignment.RIGHT) {
            // For RIGHT alignment, use the right edge of a reference line
            if (referenceLineWithContent) {
                return referenceLineWithContent.bounds.x + referenceLineWithContent.bounds.width;
            }
            return this.maxTextWidth;
        }

        // LEFT alignment: use the left edge of a reference line
        if (referenceLineWithContent) {
            return referenceLineWithContent.bounds.x;
        }
        return 0;
    }

    override resetBuild() {
        this.#textMetrics = null;
    }

    buildAnimationManagerConfig(containerDuration: number): AnimationConfig[] {
        if (!this.#textMetrics) {
            return [];
        }
        const animationConfigs: AnimationConfig[] = [];
        Object.values(this._styles).forEach((style) => {
            style.animationConfigurations.forEach((config) => {
                const animationConfig = config.buildAnimationManagerConfig(
                    this.#getInferredValues.bind(this),
                    this.#getOffsetForProperty.bind(this) as (
                        property: string,
                        shouldOffsetPropertyValue: boolean,
                        level: DynamicLevel,
                    ) => number,
                    containerDuration,
                );
                if (animationConfig) {
                    animationConfigs.push(animationConfig);
                }
            });
            animationConfigs.push(
                ...style.graphics.flatMap((graphic) =>
                    graphic.buildAnimationManagerConfig(containerDuration),
                ),
            );
        });
        return animationConfigs;
    }

    #getInferredValues(
        inferredDimension: DynamicInferredDimensions,
        level: DynamicLevel,
    ): number[] {
        const metrics = this.#getLevelMetrics(level);
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
                return [metrics[0]?.height ?? 0];
            case DynamicInferredDimensions.maxHeight:
                const maxHeight = Math.max(...metrics.map((metric) => metric.height));
                return Array(metrics.length).fill(maxHeight);

            case DynamicInferredDimensions.width:
                return [metrics[0]?.width ?? 0];
            case DynamicInferredDimensions.maxWidth:
                const maxWidth = Math.max(...metrics.map((metric) => metric.width));
                return Array(metrics.length).fill(maxWidth);

            // These are unsupported for text
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw Error(`Unsupported inferred value ${inferredDimension}`);
        }
    }

    #getLevelMetrics(level: DynamicLevel): readonly TextMetricItemAPI[] {
        switch (level) {
            case DynamicLevel.object:
                return [this.#builtMetrics];
            case DynamicLevel.line:
                return this.#builtMetrics.lines;
            case DynamicLevel.word:
                return this.#builtMetrics.wordMetricsWithoutSpaces;
            case DynamicLevel.highlight:
                throw new Error('Inferred values for the highlight level are not supported');
            default:
                throw new Error(`Unsupported dynamic level ${level}`);
        }
    }

    /**
     * Applies an offset to the driven range to create the final driven range based on shouldOffsetPropertyValue.
     */
    #getOffsetForProperty(
        property: AnimatableTextProperties,
        shouldOffsetPropertyValue: boolean,
        level: DynamicLevel,
    ): number {
        let offset = 0;
        switch (property) {
            case 'x':
                if (!shouldOffsetPropertyValue && level === DynamicLevel.object) {
                    // position always gets offset in draw, so in the case of no offset we need to 'undo' the offset
                    // this is only applicable for object level because translateX is for the whole object.
                    // Our line & word level animations do not supported setting an absolute position (they always
                    // should be offset).
                    offset = -this.translateX;
                }
                break;
            case 'y':
                if (!shouldOffsetPropertyValue && level === DynamicLevel.object) {
                    // position always gets offset in draw, so in the case of no offset we need to 'undo' the offset
                    // Our line & word level animations do not supported setting an absolute position (they always
                    // should be offset).
                    offset = -this.translateY;
                }
                break;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    // alpha is always assigned the computed value in draw, so if offsetting we offset it here
                    offset = DEFAULT_TEXT_ALPHA;
                }
                break;
            case 'scaleX':
            case 'scaleY':
                if (shouldOffsetPropertyValue) {
                    // scale is always assigned the computed value in draw, so if offsetting we offset it here
                    offset = DEFAULT_SCALE;
                }
                break;
            default:
                throw new Error(
                    `Cannot apply offset to driven range for unsupported property "${property}"`,
                );
        }
        return offset;
    }
}
